import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Measure from 'react-measure';

import { Button, Loader, Typography } from '@vivaahealth/design-system';

import {
  Video,
  Canvas,
  Wrapper,
  Container,
  Overlay,
  LoaderWrapper,
  PermissionsNotFoundWrapper,
  getOverlayOffset,
  InstructionsAndActionsWrapper,
} from './styles';
import { useCardRatio } from './useCardRatio';
import { useUserMedia } from './useUserMedia';
import * as logger from '../../logger';

const CAPTURE_OPTIONS = {
  audio: false,
  video: {
    facingMode: 'environment',
  },
};

interface Props extends React.PropsWithChildren {
  onCapture: (blob: Blob) => void;
  onGoBack: () => void;
  captureText: string;
}

const Camera: React.FC<Props> = ({
  onCapture,
  onGoBack,
  captureText,
  children,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>();
  const videoRef = useRef<HTMLVideoElement>();

  const [container, setContainer] = useState({ width: 0, height: 0 });
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [, setIsCanvasEmpty] = useState(true);

  const [mediaStream, , mediaStreamLoading] = useUserMedia(CAPTURE_OPTIONS);

  const [aspectRatio, calculateRatio] = useCardRatio(1.5);

  const { t } = useTranslation();

  if (mediaStream && videoRef.current && !videoRef.current.srcObject) {
    videoRef.current.srcObject = mediaStream;
  }

  const firstVideoTrack = mediaStream?.getVideoTracks()[0];
  const facingMode =
    firstVideoTrack?.getSettings().facingMode ||
    firstVideoTrack?.getCapabilities().facingMode;

  const isCameraFacingEnvironment =
    (facingMode ?? []).includes('environment') ||
    (facingMode as unknown as string) === 'environment'; // Sometimes this can actually be a string

  function handleResize(contentRect: any) {
    setContainer({
      width: contentRect.bounds.width,
      height: Math.round(contentRect.bounds.width / aspectRatio),
    });
  }

  function handleCanPlay() {
    if (!videoRef.current) {
      return;
    }

    calculateRatio(videoRef.current.videoHeight, videoRef.current.videoWidth);
    videoRef.current
      .play()
      .catch((error) => logger.error(error))
      .finally(() => setIsVideoPlaying(true));
  }

  function handleCapture() {
    if (!(canvasRef.current && videoRef.current)) {
      return;
    }

    const context = canvasRef.current.getContext(
      '2d',
    ) as CanvasRenderingContext2D;

    const { videoWidth, videoHeight } = videoRef.current;
    const {
      horizontal_offset: container_overlay_horizontal_offset,
      vertical_offset: container_overlay_vertical_offset,
    } = getOverlayOffset(container.height, container.width);
    const {
      horizontal_offset: video_overlay_horizontal_offset,
      vertical_offset: video_overlay_vertical_offset,
    } = getOverlayOffset(videoHeight, videoWidth);

    const cardWidth = videoWidth - 2 * video_overlay_horizontal_offset;
    const cardHeight = videoHeight - 2 * video_overlay_vertical_offset;

    // Change the canvas dimensions to fit those of the uploaded card.
    canvasRef.current.width = cardWidth;
    canvasRef.current.height = cardHeight;

    context.drawImage(
      videoRef.current,
      container_overlay_horizontal_offset,
      container_overlay_vertical_offset,
      // Because the video is condensed to the container size when it is displayed to the user, we should actually be
      // using the video's dimensions instead of the container
      cardWidth,
      cardHeight,
      0,
      0,
      cardWidth,
      cardHeight,
    );

    canvasRef.current.toBlob((blob) => onCapture(blob!), 'image/jpeg', 1);
    setIsCanvasEmpty(false);
    handleClear();
  }

  function handleClear() {
    if (!canvasRef.current) {
      return;
    }

    const context = canvasRef.current.getContext(
      '2d',
    ) as CanvasRenderingContext2D;
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setIsCanvasEmpty(true);
  }

  if (mediaStreamLoading) {
    return <Loader label={'Loading...'} />;
  }

  if (!mediaStream) {
    return (
      <Wrapper>
        <PermissionsNotFoundWrapper>
          <Typography variant="paragraph-large">
            {t('cameraAccessDeclined')}
          </Typography>
        </PermissionsNotFoundWrapper>

        <InstructionsAndActionsWrapper>
          <Button fullWidth onClick={onGoBack}>
            Go back
          </Button>
        </InstructionsAndActionsWrapper>
      </Wrapper>
    );
  }

  return (
    <>
      {!isVideoPlaying && <Loader label={'Loading...'} />}
      <Measure bounds onResize={handleResize}>
        {({ measureRef }: any) => (
          <Wrapper>
            <Container
              ref={measureRef}
              width={container.width}
              height={container.height}
              maxWidth={videoRef.current && videoRef.current.videoWidth}
              maxHeight={videoRef.current && videoRef.current.videoHeight}
            >
              <Video
                ref={videoRef as any}
                hidden={!isVideoPlaying}
                onCanPlay={handleCanPlay}
                autoPlay
                playsInline
                muted
                flipped={!isCameraFacingEnvironment}
              />

              <Overlay
                hidden={!isVideoPlaying}
                loading={mediaStreamLoading}
                containerHeight={container.height}
                containerWidth={container.width}
              />

              {mediaStreamLoading && (
                <LoaderWrapper>
                  <Loader
                    label={t('cameraLoading')}
                    color="white"
                    variant="dot"
                  />
                </LoaderWrapper>
              )}

              <Canvas
                ref={canvasRef as any}
                width={container.width}
                height={container.height}
              />
            </Container>

            {isVideoPlaying && (
              <InstructionsAndActionsWrapper>
                {children}
                <Button fullWidth onClick={handleCapture}>
                  {captureText}
                </Button>
              </InstructionsAndActionsWrapper>
            )}
          </Wrapper>
        )}
      </Measure>
    </>
  );
};

export default Camera;
